import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, ArrowLeft, Loader2, Sparkles } from "lucide-react";
import "./TeamSocialChecklistPage.css";
import nextIcon from "../../../assets/nextButtonIcon.png";
import nextIcon2 from "../../../assets/nextButtonIcon2.png";
import apiClient from "../../../api/apiClient";
import { ENDPOINTS } from "../../../api/endPoints";
import { useGlobalAlert } from "../../../context/AlertContext";
import MobileTopBar from "../../../components/MobileTopBar";
import MobileBottomBar from "../../../components/MobileBottomBar";
import DesktopTopBar from "../../../components/DesktopTopBar";
import DesktopBottomBar from "../../../components/DesktopBottomBar";
import progressMobileIconStep1 from "../../../assets/progressIcon7.png";
import progressMobileIconStep2 from "../../../assets/progressIcon6.png";
import { useSelector } from "react-redux";

const TeamSocialChecklistPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerAlert } = useGlobalAlert();
  const profile = useSelector((state) => state.user.profile);

  const [mobileStep, setMobileStep] = useState(location.state?.step || 1);
  const [loadingBio, setLoadingBio] = useState(false);

  const pawsonalityPrompts = [
    "A dog makes me feel",
    "A breed I'm drawn to is",
    "The best thing about dogs is",
    "A doggy feature I can't resist is",
    "A core dog memory I have is",
  ];

  useEffect(() => {
    const bioText =
      formData.userBio || formData.user_bio || profile?.user_bio || "";
    if (bioText && !formData.userBio) {
      setFormData((prev) => ({ ...prev, userBio: bioText }));
    }

    const savedPawsonality =
      formData.pawsonalityCheck ||
      formData.pawsonality_check ||
      formData.pawsonalityAnswers;

    if (Array.isArray(savedPawsonality) && savedPawsonality.length > 0) {
      const newRawAnswers = {};
      const newAnswersObj = {};
      let activeIdx = null;
      let customQ = "";
      let customA = "";

      savedPawsonality.forEach((str) => {
        if (typeof str !== "string") return;
        let matched = false;

        pawsonalityPrompts.forEach((promptText, idx) => {
          if (str.toLowerCase().startsWith(promptText.toLowerCase())) {
            let answer = str.substring(promptText.length).trim();
            if (answer.startsWith("is ") || answer.startsWith("are ")) {
              answer = answer.replace(/^(is|are)\s+/i, "");
            }
            newRawAnswers[idx] = answer;
            newAnswersObj[idx] = `${promptText} ${answer}`.trim();
            if (activeIdx === null) activeIdx = idx;
            matched = true;
          }
        });

        if (!matched && str.trim()) {
          activeIdx = "custom";
          const parts = str.split("?");
          if (parts.length > 1) {
            customQ = parts[0] + "?";
            customA = parts.slice(1).join("?").trim();
          } else {
            customQ = "Custom:";
            customA = str;
          }
          newAnswersObj["custom"] = str;
        }
      });

      setFormData((prev) => {
        const mergedObj = {
          ...(prev.pawsonalityAnswers || {}),
          ...newAnswersObj,
        };
        return {
          ...prev,
          pawsonalityAnswers: mergedObj,
          pawsonalityCheck: Object.values(mergedObj).filter(Boolean),
          rawPawsonalityAnswers: {
            ...(prev.rawPawsonalityAnswers || {}),
            ...newRawAnswers,
          },
          selectedPawsonalityIndex:
            prev.selectedPawsonalityIndex !== undefined
              ? prev.selectedPawsonalityIndex
              : activeIdx,
          customPawsonalityQuestion:
            customQ || prev.customPawsonalityQuestion || "",
          customPawsonalityAnswer:
            customA || prev.customPawsonalityAnswer || "",
        };
      });
    }
  }, []);

  const handleAnswerChange = (index, promptText, answerValue) => {
    const combinedValue = answerValue
      ? `${promptText} ${answerValue}`.trim()
      : "";

    setFormData((prev) => {
      const existingObj = prev.pawsonalityAnswers || {};
      const updatedObj = {
        ...existingObj,
        [index]: combinedValue,
      };
      if (!combinedValue) {
        delete updatedObj[index];
      }
      return {
        ...prev,
        pawsonalityAnswers: updatedObj,
        pawsonalityCheck: Object.values(updatedObj).filter(Boolean),
        rawPawsonalityAnswers: {
          ...(prev.rawPawsonalityAnswers || {}),
          [index]: answerValue,
        },
      };
    });
  };

  const handleCustomPromptChange = (question, answer) => {
    const q = question ? question.trim() : "";
    const a = answer ? answer.trim() : "";
    const combined = [q, a].filter(Boolean).join(" ");

    setFormData((prev) => {
      const existingObj = prev.pawsonalityAnswers || {};
      const updatedObj = {
        ...existingObj,
        custom: combined,
      };
      if (!combined) {
        delete updatedObj["custom"];
      }
      return {
        ...prev,
        customPawsonalityQuestion: question,
        customPawsonalityAnswer: answer,
        pawsonalityAnswers: updatedObj,
        pawsonalityCheck: Object.values(updatedObj).filter(Boolean),
      };
    });
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBioChange = (e) => {
    setFormData((prev) => ({ ...prev, userBio: e.target.value }));
  };

  const handleWriteBio = async () => {
    setLoadingBio(true);
    try {
      const payload = {
        name: formData.name || "User",
        what_brings_you_here: formData.selectedBringsYouHere || "",
      };

      const response = await apiClient.post(
        ENDPOINTS.GENERATE_AI_BIO_TEAMSOCIAL,
        payload,
      );

      if (response.data.success && response.data.bio) {
        setFormData((prev) => ({
          ...prev,
          userBio: response.data.bio,
        }));
      }
    } catch (error) {
      console.error("AI Bio Error:", error.response?.data || error.message);
      triggerAlert("Could not generate bio at this time.", "error");
    } finally {
      setLoadingBio(false);
    }
  };

  const isAnyPromptAnswered =
    Object.values(formData.rawPawsonalityAnswers || {}).some((val) =>
      val?.trim(),
    ) ||
    Boolean(
      formData.customPawsonalityQuestion?.trim() &&
      formData.customPawsonalityAnswer?.trim(),
    );

  const hasBioText = (formData.userBio || "").trim() !== "";
  const isFormValid = isAnyPromptAnswered && hasBioText;

  const handleNext = () => {
    if (window.innerWidth >= 1024) {
      if (!isFormValid) return;
      navigate("/onboarding-final");
    } else {
      if (mobileStep === 1) {
        if (!isAnyPromptAnswered) return;
        navigate("/dog-social-personality", { state: { step: 2 } });
      } else {
        if (!hasBioText) return;
        navigate("/onboarding-final");
      }
    }
  };

  const handleSkip = () => {
    if (window.innerWidth >= 1024) {
      navigate("/onboarding-final");
    } else {
      if (mobileStep === 1) {
        navigate("/dog-social-personality", { state: { step: 2 } });
      } else {
        navigate("/team-social-bio", { state: { step: 2 } });
      }
    }
  };

  const handleMobileMobileBack = () => {
    if (mobileStep === 1) {
      switch (formData.dogPersonalityOrigin) {
        case "half-onboarding":
          navigate("/half-onboarding-page");
          break;
        case "half-completed":
          navigate("/half-completed-page");
          break;
        default:
          navigate(-1);
      }
    } else {
      navigate("/dog-social-personality", { state: { step: 2 } });
    }
  };

  const handleMobileDesktopBack = () => {
    switch (formData.dogPersonalityOrigin) {
      case "half-onboarding":
        navigate("/half-onboarding-page");
        break;
      case "half-completed":
        navigate("/half-completed-page");
        break;
      default:
        navigate(-1);
    }
  };

  return (
    <div
      className={`tspaw-container ${mobileStep === 1 ? "tspaw-mobile-step-1" : "tspaw-mobile-step-2"}`}
    >
      {/* LEFT PANEL - Prompts (Mobile Step 1) */}
      <div
        className={`tspaw-panel tspaw-left-panel ${mobileStep === 1 ? "tspaw-mobile-active" : "tspaw-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={handleMobileMobileBack}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
          isProtectedRoute={false}
        />

        <DesktopTopBar
          showBack={true}
          onBack={handleMobileDesktopBack}
          backColor="#000"
          showProgress={false}
        />

        <div className="tspaw-content-wrapper">
          <div className="tspaw-header-section-step-1 tsp-mobile-only">
            <div className="tspaw-dog-icon-container-step-1">
              <img
                src={progressMobileIconStep1}
                alt="Progress Step"
                className="tsp-progress-icon"
              />
            </div>
            <div className="tspaw-progress-track">
              <div className="tspaw-progress-fill tspaw-step-1-fill"></div>
            </div>
          </div>
          <div className="tspaw-title-container">
            <h1 className="tspaw-title">PAWSONALITY CHECK</h1>
          </div>
          <p className="tspaw-subtitle">
            Help dog-owners get to know the dog-lover in you. Respond to as many
            prompts as you feel inspired to!
          </p>

          <div className="tspaw-list">
            {pawsonalityPrompts.map((prompt, index) => {
              const isSelected = formData.selectedPawsonalityIndex === index;
              const rawVal = formData.rawPawsonalityAnswers?.[index] || "";

              return (
                <div
                  key={index}
                  className={`tspaw-prompt-row ${isSelected ? "active" : ""}`}
                  onClick={() => updateField("selectedPawsonalityIndex", index)}
                >
                  <label className="tspaw-prompt-label">{prompt}...</label>
                  {isSelected ? (
                    <input
                      type="text"
                      className="tspaw-prompt-input"
                      autoFocus
                      placeholder="Type here..."
                      value={rawVal}
                      onChange={(e) =>
                        handleAnswerChange(index, prompt, e.target.value)
                      }
                    />
                  ) : rawVal ? (
                    <div className="tspaw-prompt-answer">{rawVal}</div>
                  ) : (
                    <div className="tspaw-underline"></div>
                  )}
                </div>
              );
            })}

            <div
              className="tspaw-write-box tspaw-bg-teal"
              style={{
                flexDirection: "column",
                gap: "5px",
                padding: "15px",
                height: "auto",
              }}
            >
              <input
                type="text"
                placeholder="Write your own"
                className="tspaw-write-input"
                style={{ width: "100%" }}
                value={formData.customPawsonalityQuestion || ""}
                onChange={(e) =>
                  handleCustomPromptChange(
                    e.target.value,
                    formData.customPawsonalityAnswer || "",
                  )
                }
              />
            </div>
          </div>
        </div>

        <div className="tspaw-mobile-footer tspaw-mobile-only">
          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={isAnyPromptAnswered}
            nextIconSrc={nextIcon2}
            showSkip={true}
            onSkip={handleSkip}
            isSkipBlue={true}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL - Bio Input (Mobile Step 2) */}
      <div
        className={`tspaw-panel tspaw-right-panel ${mobileStep === 2 ? "tspaw-mobile-active" : "tspaw-mobile-hidden"}`}
      >
        <div className="tspaw-mobile-header tspaw-mobile-only">
          <MobileTopBar
            onBack={handleMobileMobileBack}
            backColor="#000"
            logoColor="#009fd9"
            blueLogo="true"
            isProtectedRoute={false}
          />
        </div>

        <div className="tspaw-content-wrapper tspaw-right-wrapper">
          <div className="tspaw-header-section-step-2 tsp-mobile-only">
            <div className="tspaw-dog-icon-container-step-2">
              <img
                src={progressMobileIconStep2}
                alt="Progress Step"
                className="tsp-progress-icon"
              />
            </div>
            <div className="tspaw-progress-track">
              <div className="tspaw-progress-fill tspaw-step-2-fill"></div>
            </div>
          </div>
          <div className="tspaw-title-container tspaw-title-right">
            <h1 className="tspaw-title tspaw-text-white">IN YOUR OWN WORDS</h1>
          </div>
          <p className="tspaw-subtitle tspaw-text-white">
            Share more about what brings you to Social Paws, your dream dog
            setup and whatever else your heart desires!
          </p>

          <div className="tspaw-textarea-wrapper">
            <textarea
              className={`${window.innerWidth >= 1024 ? "tspaw-textarea-blue" : "tspaw-textarea-green"}`}
              placeholder="Write here..."
              value={formData.userBio || ""}
              onChange={handleBioChange}
            />

            <button
              type="button"
              className="tspaw-write-btn"
              onClick={handleWriteBio}
              disabled={loadingBio}
            >
              {loadingBio ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Sparkles size={20} strokeWidth={2} />
              )}
              {loadingBio ? "GENERATING..." : "WRITE MY BIO"}
            </button>
          </div>

          <p
            className="tspaw-help-subtitle"
            style={{
              paddingTop: "1rem",
              textAlign: "left",
            }}
          >
            Use the AI button to move through this faster, and come back to
            update it anytime!
          </p>

          <DesktopBottomBar
            onNext={handleNext}
            isNextEnabled={isFormValid}
            nextIconSrc={nextIcon}
            variant="green"
          />

          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={hasBioText}
            nextIconSrc={nextIcon2}
            showSkip={false}
            variant="blue"
          />
        </div>
      </div>
    </div>
  );
};

export default TeamSocialChecklistPage;
